import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'react-toastify';
import SubscriptionTable from './SubscriptionTable';
import SubscriptionSearch from './SubscriptionSearch';
import SubscriptionStats from './SubscriptionStats';
import SubscriptionPagination from './SubscriptionPagination';
import SubscriptionFilterSidebar from './SubscriptionFilterSidebar';
import subscriptionRequests from '../../api/SubscriptionRequests';
import {
  Users,
  CreditCard,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  DollarSign,
  SlidersHorizontal,
  Download
} from 'lucide-react';

const SubscriptionDataTable = ({ subscriptions, loading }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('all');
  const [statusFilter, setStatusFilter] = useState('paid');
  const [planFilter, setPlanFilter] = useState('all');
  const [networkFilter, setNetworkFilter] = useState('all');
  const [packageFilter, setPackageFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Helper function to determine subscription status
  const getSubscriptionStatus = (subscription) => {
    // First check if it's a trial
    if (subscription.plan_name === 'Free Trial') {
      return 'trial';
    }
    console.log("Checking plan_name status for subscription:", subscription);
    
    // Check various possible status fields
    if (subscription.status) {
      const plan_name = subscription.plan_name ? subscription.plan_name.toLowerCase() : '';
      const status = subscription.status.toLowerCase();
      if ((status.includes('paid') || status === 'active' || status === 'completed') && !plan_name.includes('trial plan')) {
        return 'paid';
      }
      if (status.includes('unpaid') || status === 'pending' || status === 'due') {
        return 'unpaid';
      }
      if (status.includes('failed') || status === 'cancelled' || status === 'expired') {
        return 'failed';
      }
    }
    
    // Check marked_as_paid field
    if (subscription.marked_as_paid === '1' || subscription.marked_as_paid === true || subscription.marked_as_paid === 1) {
      return 'paid';
    }
    if (subscription.marked_as_paid === '0' || subscription.marked_as_paid === false || subscription.marked_as_paid === 0) {
      return 'unpaid';
    }
    
    // Check payment_status field if exists
    if (subscription.payment_status) {
      const paymentStatus = subscription.payment_status.toLowerCase();
      if (paymentStatus.includes('paid') || paymentStatus === 'success') {
        return 'paid';
      }
      if (paymentStatus.includes('unpaid') || paymentStatus === 'pending') {
        return 'unpaid';
      }
    }
    
    // Default to unpaid if no status detected
    return 'unpaid';
  };

  // Memoized filtered and sorted data
  const filteredSubscriptions = useMemo(() => {
    let filtered = subscriptions;

    // Search filter — type-aware, and checks the top-level field first since
    // that's where email/phone actually live on these records (sub.user is
    // often undefined; see SubscriptionTableRow's display logic).
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(sub => {
        const name  = (sub.user?.full_name || '').toLowerCase();
        const email = (sub.email || sub.user?.email || '').toLowerCase();
        const phone = sub.phone_number || sub.user?.phone_number || '';
        const plan  = (sub.plan_name || '').toLowerCase();
        const slug  = (sub.user_slug || '').toLowerCase();

        switch (searchType) {
          case 'email':
            return email.includes(term);
          case 'phone':
            return phone.includes(searchTerm);
          case 'plan':
            return plan.includes(term);
          default:
            return (
              name.includes(term) ||
              email.includes(term) ||
              phone.includes(searchTerm) ||
              plan.includes(term) ||
              slug.includes(term)
            );
        }
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sub => {
        const status = getSubscriptionStatus(sub);
        return status === statusFilter;
      });
    }

    // Plan filter
    if (planFilter !== 'all') {
      filtered = filtered.filter(sub => sub.plan_name === planFilter);
    }

    // Network filter
    if (networkFilter !== 'all') {
      filtered = filtered.filter(sub => sub.network === networkFilter);
    }

    // Package filter
    if (packageFilter !== 'all') {
      filtered = filtered.filter(sub => sub.package_name === packageFilter);
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'full_name':
          aValue = a.user?.full_name || '';
          bValue = b.user?.full_name || '';
          break;
        case 'email':
          aValue = a.user?.email || '';
          bValue = b.user?.email || '';
          break;
        case 'plan_name':
          aValue = a.plan_name || '';
          bValue = b.plan_name || '';
          break;
        case 'amount':
          aValue = parseFloat(a.amount) || 0;
          bValue = parseFloat(b.amount) || 0;
          break;
        case 'status':
          aValue = getSubscriptionStatus(a);
          bValue = getSubscriptionStatus(b);
          break;
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'due':
          aValue = new Date(a.due);
          bValue = new Date(b.due);
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [subscriptions, searchTerm, searchType, statusFilter, planFilter, networkFilter, packageFilter, sortConfig]);

  // Pagination logic
  const totalItems = filteredSubscriptions.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSubscriptions = filteredSubscriptions.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, searchType, statusFilter, planFilter, networkFilter, packageFilter]);

  // Calculate statistics
  const stats = useMemo(() => {
    const total = subscriptions.length;
    
    // Get counts for each status
    const paid = subscriptions.filter(sub => getSubscriptionStatus(sub) === 'paid').length;
    const unpaid = subscriptions.filter(sub => getSubscriptionStatus(sub) === 'unpaid').length;
    const trials = subscriptions.filter(sub => getSubscriptionStatus(sub) === 'trial').length;
    const failed = subscriptions.filter(sub => getSubscriptionStatus(sub) === 'failed').length;
    
    // Calculate revenue
    const totalRevenue = subscriptions
      .filter(sub => getSubscriptionStatus(sub) === 'paid')
      .reduce((sum, sub) => sum + (parseFloat(sub.amount) || 0), 0);
    
    const pendingRevenue = subscriptions
      .filter(sub => getSubscriptionStatus(sub) === 'unpaid')
      .reduce((sum, sub) => sum + (parseFloat(sub.amount) || 0), 0);

    return {
      total,
      paid,
      unpaid,
      trials,
      failed,
      totalRevenue,
      pendingRevenue
    };
  }, [subscriptions]);

  // Get unique plans for filter
  const uniquePlans = useMemo(() => {
    const plans = [...new Set(subscriptions.map(sub => sub.plan_name).filter(Boolean))];
    return plans.sort();
  }, [subscriptions]);

  // Get unique packages for filter
  const uniquePackages = useMemo(() => {
    const packages = [...new Set(subscriptions.map(sub => sub.package_name).filter(Boolean))];
    return packages.sort();
  }, [subscriptions]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSearchType('all');
    setStatusFilter('all');
    setPlanFilter('all');
    setNetworkFilter('all');
    setPackageFilter('all');
    setSortConfig({ key: 'created_at', direction: 'desc' });
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Export the currently-filtered view. Params mirror whatever is active in
  // the filters/search above. Note: when searchType is 'all' we don't know
  // whether the typed text was meant as an email or a phone number, so
  // neither export param gets populated in that case — pick a specific
  // search type (Email/Phone) first if you need that in the export.
  const handleExport = async () => {
    setIsExporting(true);
    const toastId = toast.loading('Preparing export...');
    try {
      const filters = {
        status: statusFilter !== 'all' ? statusFilter : '',
        plan_name: planFilter !== 'all' ? planFilter : '',
        network: networkFilter !== 'all' ? networkFilter : '',
        package_name: packageFilter !== 'all' ? packageFilter : '',
        email: searchType === 'email' && searchTerm ? searchTerm : '',
        phone_number: searchType === 'phone' && searchTerm ? searchTerm : '',
      };

      const result = await subscriptionRequests.exportSubscriptions(filters);

      if (result.status) {
        toast.update(toastId, {
          render: 'Export completed successfully!',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      } else {
        toast.update(toastId, {
          render: result.message || 'Export failed',
          type: 'error',
          isLoading: false,
          autoClose: 5000,
        });
      }
    } catch (error) {
      toast.update(toastId, {
        render: 'Failed to export data',
        type: 'error',
        isLoading: false,
        autoClose: 5000,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <SubscriptionStats stats={stats} />

      {/* Search and Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1">
          <SubscriptionSearch
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            searchType={searchType}
            onSearchTypeChange={setSearchType}
          />
        </div>
        <div className="flex gap-2 items-center">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleExport}
            disabled={isExporting || loading}
            className="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            {isExporting ? 'Exporting...' : 'Export'}
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setSidebarOpen(true)}
            className="inline-flex gap-2 items-center px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 rounded-lg border border-purple-200 shadow-sm transition-colors hover:bg-purple-100"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
          </motion.button>
        </div>
      </div>

      {/* Results Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-wrap gap-4 justify-between items-center p-4 bg-white rounded-xl border border-gray-100 shadow-sm"
      >
        <div className="flex gap-2 items-center">
          <Users className="w-5 h-5 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">
            Showing {Math.min(filteredSubscriptions.length, itemsPerPage)} of {filteredSubscriptions.length} subscriptions (filtered from {subscriptions.length} total)
          </span>
        </div>
        
        {(searchTerm || statusFilter !== 'all' || planFilter !== 'all' || networkFilter !== 'all' || packageFilter !== 'all') && (
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={clearFilters}
            className="px-3 py-1 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
          >
            Clear Filters
          </motion.button>
        )}
      </motion.div>

      {/* Data Table */}
      <AnimatePresence mode="wait">
        <motion.div
          key={`${searchTerm}-${searchType}-${statusFilter}-${planFilter}-${networkFilter}-${packageFilter}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <SubscriptionTable
            subscriptions={paginatedSubscriptions}
            loading={loading}
            sortConfig={sortConfig}
            onSort={handleSort}
            getSubscriptionStatus={getSubscriptionStatus}
          />
        </motion.div>
      </AnimatePresence>

      {/* Pagination */}
      {totalPages > 1 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <SubscriptionPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </motion.div>
      )}

      {/* Filter Sidebar */}
      <SubscriptionFilterSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        statusFilter={statusFilter}
        planFilter={planFilter}
        networkFilter={networkFilter}
        packageFilter={packageFilter}
        onStatusChange={setStatusFilter}
        onPlanChange={setPlanFilter}
        onNetworkChange={setNetworkFilter}
        onPackageChange={setPackageFilter}
        onClearFilters={clearFilters}
        subscriptions={subscriptions}
        uniquePlans={uniquePlans}
        uniquePackages={uniquePackages}
        getSubscriptionStatus={getSubscriptionStatus}
      />
    </div>
  );
};

export default SubscriptionDataTable;